import { AddressParams, transformAddressToRequest } from 'api/account';
import Axios from 'api/client';
import { ContactType } from 'constants/contacts';
import { sanitizeEmptyStrings } from 'helpers/objects';
import {
    ArchiveReason,
    Contact,
    ContactEmail,
    ContactFile,
    ContactGender,
    ContactPhone,
    CustomFieldRequestValue,
} from 'types/domain';
import { PaginatedData } from 'types/pagination';

export interface CreateContactParams {
    firstName?: string;
    middleName?: string;
    lastName?: string;
    dob?: string | null;
    allowEngageEmail?: boolean;
    allowEngageSms?: boolean;
    allowEngageAddress?: boolean;
    employer?: string;
    title?: string;
    emails: ContactEmail[];
    phones: ContactPhone[];
    note?: string;
    prefix?: string | null;
    suffix?: string | null;
    gender?: ContactGender | null;
    addresses?: (AddressParams & { id?: string; isPrimary?: boolean })[];
    customFields?: {
        [field: string]: CustomFieldRequestValue;
    };
    type: ContactType;
    companyName?: string;
    company?: string;
}

export interface UpdateContactParams extends Partial<CreateContactParams> {
    websiteUrl?: string;
    linkedInUrl?: string;
    twitterUrl?: string;
    facebookUrl?: string;
    tags?: string[];
}

interface PostContactArchiveParams {
    isArchived: boolean;
}

interface UpdateContactNoteParams {
    accountId: number;
    contactId: number;
    note: string;
}

interface AddNewActivityParams {
    accountId: number;
    contactId: number;
    data: object;
    type: string;
}

interface AddNewActivityParams {
    accountId: number;
    contactId: number;
    data: object;
    type: string;
}

interface EditActivityParams<T> {
    accountId: number;
    contactId: number;
    activityId: number;
    data: T;
}

interface AddTagsParams {
    accountId: number;
    contactsIds: number[];
    tags: string[];
}

interface DeleteTagsParams {
    accountId: number;
    contactsIds: number[];
    tags: string[];
}

interface ArchiveContactsParams {
    accountId: number;
    contactsIds: number[];
}

interface UnarchiveContactsParams {
    accountId: number;
    contactsIds: number[];
}

interface FetchDonorSearchParams {
    refresh: boolean;
}

export interface FetchContactFilesParams {
    limit?: number;
    page?: number;
}

interface ArchiveContactParams {
    reason: ArchiveReason;
    note: string;
}

export const fetchContact = (accountId: number, contactId: number) =>
    Axios.get<Contact>(`/accounts/${accountId}/contacts/${contactId}`).then(
        response => response.data,
    );

export const createContact = (accountId: number, params: CreateContactParams) =>
    Axios.post<Contact>(
        `/accounts/${accountId}/contacts`,
        transformContactParamsIntoRequest(params),
    );

export const updateContact = (accountId: number, contactId: number, params: UpdateContactParams) =>
    Axios.patch<Contact>(
        `/accounts/${accountId}/contacts/${contactId}`,
        transformContactParamsIntoRequest(params),
    );

export const transformContactParamsIntoRequest = (params: UpdateContactParams) => {
    const sanitized = sanitizeEmptyStrings(params);
    const emails = sanitized?.emails?.map(sanitizeEmptyStrings);
    const phones = sanitized?.phones?.map(sanitizeEmptyStrings);
    const addresses = sanitized.addresses?.map(address =>
        sanitizeEmptyStrings({
            id: address.id,
            ...transformAddressToRequest(address),
        }),
    );

    return {
        first_name: params.firstName,
        middle_name: params.middleName,
        last_name: params.lastName,
        dob: params.dob,
        email_subscription: sanitized.allowEngageEmail,
        phone_subscription: sanitized.allowEngageSms,
        address_subscription: sanitized.allowEngageAddress,
        prefix: params?.prefix,
        suffix: params?.suffix,
        gender: params?.gender,
        employer: params?.employer,
        company: params?.company,
        title: params.title,
        website_url: params?.websiteUrl,
        twitter_url: params?.twitterUrl,
        linkedin_url: params?.linkedInUrl,
        facebook_url: params?.facebookUrl,
        note: params?.note,
        tags: sanitized.tags,
        emails,
        phones,
        addresses,
        custom_fields: sanitized?.customFields,
        type: params?.type,
        company_name: params?.companyName,
    };
};

export const postContactArchive = (
    accountId: number,
    contactId: number,
    { isArchived }: PostContactArchiveParams,
) =>
    Axios.post(
        `/accounts/${accountId}/contacts/${contactId}/${isArchived ? 'unarchive' : 'archive'}`,
    );

export const deleteContactAddress = (accountId: number, contactId: number, addressId: number) =>
    Axios.delete(`/accounts/${accountId}/contacts/${contactId}/addresses/${addressId}`);

export const updateContactNote = ({ accountId, contactId, note }: UpdateContactNoteParams) =>
    Axios.patch(`/accounts/${accountId}/contacts/${contactId}/note`, {
        note,
    });

export const addNewActivity = ({ accountId, contactId, data, type }: AddNewActivityParams) =>
    Axios.post(`/accounts/${accountId}/contacts/${contactId}/activities`, {
        type,
        ...data,
    });

export const editActivity = <T>({
    accountId,
    contactId,
    activityId,
    data,
}: EditActivityParams<T>) =>
    Axios.patch(`/accounts/${accountId}/contacts/${contactId}/activities/${activityId}`, data);

export const addTags = ({ accountId, tags, contactsIds }: AddTagsParams) =>
    Axios.post(`/accounts/${accountId}/contacts/tags/bulk`, {
        tags,
        contact_ids: contactsIds,
    });

export const deleteTags = ({ accountId, tags, contactsIds }: DeleteTagsParams) =>
    Axios.delete(`/accounts/${accountId}/contacts/tags/bulk`, {
        data: {
            tags,
            contact_ids: contactsIds,
        },
    });

export const archiveContacts = ({ accountId, contactsIds }: ArchiveContactsParams) =>
    Axios.post(`/accounts/${accountId}/contacts/archive/bulk`, {
        contact_ids: contactsIds,
    });

export const unarchiveContacts = ({ accountId, contactsIds }: UnarchiveContactsParams) =>
    Axios.delete(`/accounts/${accountId}/contacts/unarchive/bulk`, {
        data: {
            contact_ids: contactsIds,
        },
    }).then(response => response.data);

export const performDonorSearch = (
    accountId: number,
    contactId: number,
    { refresh }: FetchDonorSearchParams,
) =>
    Axios.post(`/accounts/${accountId}/contacts/${contactId}/donorsearch`, { refresh }).then(
        response => response.data,
    );

export const deleteDonorSearch = (accountId: number, contactId: number) =>
    Axios.delete(`/accounts/${accountId}/contacts/${contactId}/donorsearch`);

export const getDonorSearchIntegration = (accountId: number) =>
    Axios.get(`/accounts/${accountId}/integrations/donorsearch`)
        .then(response => response.data)
        .catch(() => null);

export interface ContactAddressRequest extends AddressParams {
    isPrimary?: boolean;
    type: string;
}

const transformAddressFormToRequest = (address: Partial<ContactAddressRequest>) => ({
    address_1: address.line1,
    address_2: address.line2,
    city: address.city,
    state: address.state,
    zipcode: address.zip,
    country: address.country,
    type: address.type,
    is_primary: !!address.isPrimary,
});

export const createContactAddress = (
    accountId: number,
    contactId: number,
    address: ContactAddressRequest,
) =>
    Axios.post(
        `/accounts/${accountId}/contacts/${contactId}/addresses`,
        transformAddressFormToRequest(address),
    );

export const updateContactAddress = (
    accountId: number,
    contactId: number,
    addressId: number,
    address: Partial<ContactAddressRequest>,
) =>
    Axios.patch(
        `/accounts/${accountId}/contacts/${contactId}/addresses/${addressId}`,
        transformAddressFormToRequest(address),
    );

export const fetchContactFiles = (
    accountId: number,
    contactId: number,
    params: FetchContactFilesParams,
) =>
    Axios.get<PaginatedData<ContactFile>>(`/accounts/${accountId}/contacts/${contactId}/files`, {
        params,
    }).then(res => res.data);

export const uploadContactFile = (accountId: number, contactId: number, formData: FormData) =>
    Axios.post(`/accounts/${accountId}/contacts/${contactId}/files`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });

export const renameContactFile = (
    accountId: number,
    contactId: number,
    file: Pick<ContactFile, 'id' | 'name'>,
) => Axios.patch(`/accounts/${accountId}/contacts/${contactId}/files/${file.id}`, file);

export const deleteContactFile = (accountId: number, contactId: number, fileId: number) =>
    Axios.delete(`/accounts/${accountId}/contacts/${contactId}/files/${fileId}`);

export const archiveContact = (accountId: number, contactId: number, data: ArchiveContactParams) =>
    Axios.post(`/accounts/${accountId}/contacts/${contactId}/archive`, data);

export const deleteContact = (accountId: number, contactId: number) =>
    Axios.delete(`/accounts/${accountId}/contacts/${contactId}`);
